import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ArrowRight, CheckCircle2, Clock3, Crown, Mail, MailCheck, RotateCcw,
  ShieldCheck, UserPlus, Users, X,
} from 'lucide-react';
import { fetchApi } from '../api';
import '../styles/account.css';

type TeamRole = 'OWNER' | 'ADMIN' | 'AGENT';
type MemberStatus = 'active' | 'suspended';
interface TeamMember { id: number; role: TeamRole; status: MemberStatus; user: { id: number; name: string; email: string; emailVerified?: boolean }; }
interface PendingInvite { id: number; email: string; role: TeamRole; roleName: string; status: string; expiresAt: string; createdAt: string; invitedByName: string | null; }
interface Seats { used: number; pendingInvites: number; limit: number | null; unlimited: boolean; plan: string; planName: string; downgraded: boolean; }
interface Workspace { id: number; name: string; members: TeamMember[]; invites: PendingInvite[]; seats: Seats; }

const initials = (name: string) => name.split(/\s+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase();

const daysLeft = (iso: string) => {
  const diff = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (diff <= 0) return 'истекает сегодня';
  if (diff === 1) return 'остался 1 день';
  if (diff < 5) return `осталось ${diff} дня`;
  return `осталось ${diff} дней`;
};

export default function TeamPage() {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  /** Count only — deciding happens on the dedicated `approvals` screen. */
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'AGENT'>('AGENT');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [inviteBusyId, setInviteBusyId] = useState<number | null>(null);

  const load = async () => {
    try {
      const team = await fetchApi('/team');
      setWorkspace(team); setError('');
      // The approvals queue has its own screen; here it is only a signpost, so a
      // failure to read the count must not take the team page down with it.
      const pending = await fetchApi('/approvals').catch(() => []);
      setPendingApprovals(Array.isArray(pending) ? pending.length : 0);
    } catch (err: any) { setError(err.message || 'Не удалось загрузить команду. Проверьте права доступа.'); }
    finally { setPageLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const activeMembers = useMemo(() => workspace?.members?.filter(member => member.status === 'active').length || 0, [workspace]);
  const seats = workspace?.seats;
  const invites = workspace?.invites || [];
  const seatsTaken = (seats?.used || 0) + (seats?.pendingInvites || 0);
  const seatsFull = Boolean(seats && !seats.unlimited && seats.limit !== null && seatsTaken >= seats.limit);
  const seatFillPercent = seats && !seats.unlimited && seats.limit
    ? Math.min(100, Math.round((seatsTaken / seats.limit) * 100))
    : 0;

  /** Primary path: a letter with a link, works whether or not the person is registered. */
  const sendInvite = async (event: FormEvent) => {
    event.preventDefault(); setLoading(true); setNotice(''); setError('');
    try {
      const result = await fetchApi('/team/invites', { method: 'POST', body: JSON.stringify({ email: email.trim(), role }) });
      setEmail(''); setNotice(result?.message || 'Приглашение отправлено'); await load();
    } catch (err: any) { setError(err.message || 'Не удалось отправить приглашение'); }
    finally { setLoading(false); }
  };

  /** Kept because it is convenient for a colleague who already has an account. */
  const addExistingUser = async () => {
    if (!email.trim()) { setError('Укажите email сотрудника'); return; }
    setLoading(true); setNotice(''); setError('');
    try {
      await fetchApi('/team/members', { method: 'POST', body: JSON.stringify({ email: email.trim(), role }) });
      setEmail(''); setNotice('Сотрудник добавлен в рабочее пространство без письма'); await load();
    } catch (err: any) { setError(err.message || 'Не удалось добавить сотрудника'); }
    finally { setLoading(false); }
  };

  const resendInvite = async (id: number) => {
    setInviteBusyId(id); setNotice(''); setError('');
    try {
      const result = await fetchApi(`/team/invites/${id}/resend`, { method: 'POST' });
      setNotice(result?.message || 'Письмо отправлено повторно'); await load();
    } catch (err: any) { setError(err.message || 'Не удалось отправить письмо'); }
    finally { setInviteBusyId(null); }
  };

  const revokeInvite = async (id: number) => {
    setInviteBusyId(id); setNotice(''); setError('');
    try {
      await fetchApi(`/team/invites/${id}`, { method: 'DELETE' });
      setNotice('Приглашение отозвано — ссылка из письма больше не работает'); await load();
    } catch (err: any) { setError(err.message || 'Не удалось отозвать приглашение'); }
    finally { setInviteBusyId(null); }
  };

  const updateMember = async (id: number, data: Partial<Pick<TeamMember, 'role' | 'status'>>) => {
    setBusyId(id); setNotice(''); setError('');
    try { await fetchApi(`/team/members/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); setNotice('Изменения сохранены'); await load(); }
    catch (err: any) { setError(err.message || 'Не удалось обновить участника'); }
    finally { setBusyId(null); }
  };
  if (pageLoading) return <section className="view active workspace-page" aria-busy="true"><div className="team-skeleton team-skeleton-head"/><div className="team-skeleton team-skeleton-card"/><div className="team-skeleton team-skeleton-card"/></section>;

  return <section className="view active workspace-page">
    <div className="workspace-hero">
      <div><span className="eyebrow">Управление доступом</span><h1>Команда и согласования</h1><p>Роли сотрудников и чувствительные действия ИИ — в одном прозрачном пространстве.</p></div>
      <div className="workspace-hero-meta"><span className="workspace-shield"><ShieldCheck size={20}/></span><div><small>Рабочее пространство</small><strong>{workspace?.name || 'Clientix'}</strong></div></div>
    </div>
    {(error || notice) && <div className={`inline-feedback ${error ? 'error' : 'success'}`} role={error ? 'alert' : 'status'}>{error ? <AlertCircle size={18}/> : <CheckCircle2 size={18}/>}<span>{error || notice}</span><button type="button" aria-label="Закрыть сообщение" onClick={() => { setError(''); setNotice(''); }}><X size={16}/></button></div>}
    <div className="team-metrics" aria-label="Сводка по команде">
      <article><span className="metric-icon blue"><Users size={19}/></span><div><strong>{workspace?.members?.length || 0}</strong><span>участников</span></div></article>
      <article><span className="metric-icon green"><CheckCircle2 size={19}/></span><div><strong>{activeMembers}</strong><span>активны</span></div></article>
      <article><span className="metric-icon amber"><Clock3 size={19}/></span><div><strong>{pendingApprovals}</strong><span>ждут решения</span></div></article>
    </div>
    <div className="team-layout"><div className="team-main-column">
      <article className="team-panel">
        <header className="team-panel-head"><div><h2>Участники</h2><p>Настраивайте роли и доступ к данным клиентов.</p></div><span className="count-chip">{workspace?.members?.length || 0}</span></header>

        {/* Seats are sold by the plan, so the person paying has to see the counter. */}
        {seats && (
          <div className="seat-card">
            <div className="seat-card-head">
              <strong>Места по тарифу «{seats.planName}»</strong>
              <span className={`seat-count${seatsFull ? ' full' : ''}`}>
                {seats.used}{seats.pendingInvites > 0 ? ` + ${seats.pendingInvites}` : ''} / {seats.unlimited ? '∞' : seats.limit}
              </span>
            </div>
            {!seats.unlimited && (
              <div className="seat-meter-bar">
                <div
                  className={`seat-meter-fill${seatsFull ? ' full' : seatFillPercent >= 80 ? ' warn' : ''}`}
                  style={{ width: `${seatFillPercent}%` }}
                />
              </div>
            )}
            <p className="seat-note">
              Владелец занимает одно место.{' '}
              {seats.pendingInvites > 0 && <>Отправленные приглашения удерживают <b>{seats.pendingInvites}</b> {seats.pendingInvites === 1 ? 'место' : 'места'} до принятия или отзыва.{' '}</>}
              {seats.unlimited
                ? 'На этом тарифе число участников не ограничено.'
                : seatsFull
                  ? <b>Места закончились: отзовите приглашение, удалите участника или перейдите на тариф выше.</b>
                  : `Свободно ${Math.max(0, (seats.limit || 0) - seatsTaken)}.`}
              {seats.downgraded && <> Оплаченный период закончился, поэтому действуют лимиты бесплатного тарифа.</>}
            </p>
          </div>
        )}

        <form className="invite-form" onSubmit={sendInvite}>
          <div className="invite-copy"><span className="invite-icon"><UserPlus size={19}/></span><div><strong>Пригласить сотрудника</strong><small>Отправим письмо со ссылкой — регистрация не нужна заранее</small></div></div>
          <label className="field-shell"><span>Email</span><div><Mail size={17}/><input type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="manager@company.ru" autoComplete="email"/></div></label>
          <label className="field-shell compact"><span>Роль</span><select value={role} onChange={event => setRole(event.target.value as 'ADMIN' | 'AGENT')}><option value="AGENT">Менеджер</option><option value="ADMIN">Администратор</option></select></label>
          <button className="btn btn-primary invite-submit" disabled={loading || seatsFull}><Mail size={16}/>{loading ? 'Отправляем…' : 'Отправить приглашение'}</button>
        </form>
        <div className="invite-secondary">
          <small>Ссылка действует 7 дней и срабатывает один раз.</small>
          <button type="button" onClick={addExistingUser} disabled={loading || seatsFull}>
            Добавить без письма (если аккаунт уже есть)
          </button>
        </div>

        <div className="member-list">{workspace?.members?.map(member => { const isOwner = member.role === 'OWNER'; return <div className={`member-row ${member.status === 'suspended' ? 'is-suspended' : ''}`} key={member.id}>
          <div className="member-person"><span className="member-avatar">{initials(member.user.name)}</span><div><strong>{member.user.name}</strong><span>{member.user.email}</span></div></div>
          <span className={`status-badge ${member.status}`}><i/>{member.status === 'active' ? 'Активен' : 'Приостановлен'}</span>
          <label className="role-control"><span className="sr-only">Роль пользователя {member.user.name}</span><select value={member.role} disabled={isOwner || busyId === member.id} onChange={event => updateMember(member.id, { role: event.target.value as TeamRole })}><option value="OWNER">Владелец</option><option value="ADMIN">Администратор</option><option value="AGENT">Менеджер</option></select>{isOwner && <Crown size={15} aria-label="Владелец пространства"/>}</label>
          <button className="member-action" type="button" disabled={isOwner || busyId === member.id} onClick={() => updateMember(member.id, { status: member.status === 'active' ? 'suspended' : 'active' })}>{member.status === 'active' ? 'Приостановить' : 'Активировать'}</button>
        </div>; })}</div>
      </article>

      <article className="team-panel">
        <header className="team-panel-head"><div><h2>Отправленные приглашения</h2><p>Ссылка приходит на email и срабатывает один раз.</p></div><span className={`count-chip ${invites.length ? 'attention' : ''}`}>{invites.length}</span></header>
        {invites.length === 0
          ? <div className="invite-empty">
              <span><MailCheck size={22}/></span>
              <div>
                <strong>Активных приглашений нет</strong>
                <p>Введите email в форме выше — сотрудник получит письмо со ссылкой, зарегистрируется или войдёт под своим аккаунтом и сразу окажется в команде. Регистрироваться заранее не нужно.</p>
              </div>
            </div>
          : <div className="invite-list">{invites.map(item => <div className="invite-row" key={item.id}>
              <div className="invite-person">
                <span><Mail size={16}/></span>
                <div><strong>{item.email}</strong><small>{item.invitedByName ? `Пригласил: ${item.invitedByName}` : 'Приглашение отправлено'}</small></div>
              </div>
              <span className="status-badge pending"><i/>{item.roleName}</span>
              <div className="invite-expiry">
                <b>{daysLeft(item.expiresAt)}</b>
                до {new Date(item.expiresAt).toLocaleDateString('ru-RU')}
              </div>
              <div className="invite-row-actions">
                <button type="button" onClick={() => resendInvite(item.id)} disabled={inviteBusyId === item.id}>
                  <RotateCcw size={13}/> Отправить снова
                </button>
                <button type="button" className="danger" onClick={() => revokeInvite(item.id)} disabled={inviteBusyId === item.id}>
                  <X size={13}/> Отозвать
                </button>
              </div>
            </div>)}</div>}
      </article>

      {/* Deciding lives on the dedicated `approvals` screen together with the AI
          action audit; duplicating the decision buttons here would give two
          places to keep in step. */}
      <article className="team-panel">
        <header className="team-panel-head"><div><h2>Согласования действий ИИ</h2><p>Запросы, где ИИ ждёт разрешения человека, и журнал уже выполненных действий.</p></div><span className={`count-chip ${pendingApprovals ? 'attention' : ''}`}>{pendingApprovals}</span></header>
        <div className="invite-empty">
          <span>{pendingApprovals ? <Clock3 size={22}/> : <CheckCircle2 size={22}/>}</span>
          <div>
            <strong>{pendingApprovals
              ? `${pendingApprovals} ${pendingApprovals === 1 ? 'запрос ждёт' : 'запроса ждут'} вашего решения`
              : 'Нет действий, которые требуют вашего решения'}</strong>
            <p>Полный экран согласований показывает контекст диалога, историю решений и журнал действий ИИ.</p>
            <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 10 }} onClick={() => { window.location.hash = 'approvals'; }}>
              Открыть согласования <ArrowRight size={15}/>
            </button>
          </div>
        </div>
      </article>
    </div><aside className="access-guide"><span className="access-guide-icon"><ShieldCheck size={22}/></span><h2>Уровни доступа</h2><p>Роли определяют, какие данные и настройки доступны сотруднику.</p><div className="access-role"><Crown size={16}/><div><strong>Владелец</strong><span>Полный доступ и оплата</span></div></div><div className="access-role"><ShieldCheck size={16}/><div><strong>Администратор</strong><span>Команда, настройки и аналитика</span></div></div><div className="access-role"><Users size={16}/><div><strong>Менеджер</strong><span>Клиенты, чаты и сделки</span></div></div><div className="access-note">Изменения ролей применяются сразу и влияют на все активные сессии.</div></aside></div>
  </section>;
}
